import { ConfirmSmall } from '@wix/wix-ui-icons-common';
import { embeddedScriptAppStatus } from '../../manage-installed-apps-page';
import type {
  BadgeSkin,
  BadgeType,
  BadgeSize,
  IconElement,
} from '@wix/design-system';
import { Badge, Tooltip, FloatingHelper } from '@wix/design-system';
import {
  canUsePremiumApp,
  isBundleApp,
  isBundleAppAndInstalled,
  isBundleAppAndNotInstalled,
  isBundleAppFromVoucher,
  isEmbeddedScriptApp,
  isHotelsAndNotPending,
  isNewVersionAvailable,
  isPendingApp,
  isSetupIncomplete,
} from '../../..';
import React, { useState, useRef } from 'react';
import type { InjectedExperimentsProps } from '@wix/wix-experiments-react';
import { useExperiments } from '@wix/wix-experiments-react';
import type { BundleWebSolution } from '@wix/ambassador-marketplace/types';
import type { ManagedApp } from '@wix/ambassador-devcenter-managedapps-v1-managed-app/types';
import { useTranslation } from '../../../hooks/use-translation';

interface IAppBadgeProps {
  app: ManagedApp;
  appEmbeddedScriptAppsStatus: embeddedScriptAppStatus;
  siteIsPremium: boolean;
  siteIsBusinessFirstSite?: boolean;
  bundleApps: BundleWebSolution[];
}

const getLabel = (
  app: ManagedApp,
  appEmbeddedScriptAppsStatus: embeddedScriptAppStatus,
  t,
  siteIsPremium: boolean,
  experiments: InjectedExperimentsProps['experiments'],
  siteIsBusinessFirstSite: boolean,
  bundleApps,
) => {
  const appId = app?.appId;
  if (isHotelsAndNotPending(app, experiments)) {
    return t('manage-apps.status.badge.Hotels-beta');
  }
  if (
    isBundleAppAndInstalled(appId, bundleApps) &&
    isBundleAppFromVoucher(appId, bundleApps)
  ) {
    return t('manage-apps.status.badge.bundle-voucher-installed');
  }

  if (isBundleApp(appId, bundleApps)) {
    return t('manage-apps.status.badge.bundle');
  }

  if (!canUsePremiumApp(app, siteIsPremium)) {
    return t('manage-apps.status.badge.premium');
  }

  if (isNewVersionAvailable(app)) {
    return t('manage-apps.status.badge.update-version');
  }

  if (isPendingApp(app)) {
    if (siteIsBusinessFirstSite) {
      return t('manage-apps.status.badge.site-pending');
    }
    return t('manage-apps.status.badge.Pending');
  }

  if (isSetupIncomplete(app as ManagedApp)) {
    return t('manage-apps.status.badge.incomplete-setup');
  }

  if (isEmbeddedScriptApp(app)) {
    switch (appEmbeddedScriptAppsStatus) {
      case embeddedScriptAppStatus.ENABLED:
        return t('manage-apps.status.badge.running');
      default:
        return t('manage-apps.status.badge.Paused');
    }
  }
};

const getTheme = (
  app: ManagedApp,
  appEmbeddedScriptAppsStatus: embeddedScriptAppStatus,
  siteIsPremium: boolean,
  experiments: InjectedExperimentsProps['experiments'],
  bundleApps,
): {
  skin: BadgeSkin;
  type: BadgeType;
  size: BadgeSize;
  uppercase: boolean;
  prefixIcon?: IconElement;
} => {
  const appId = app?.appId;

  if (isHotelsAndNotPending(app, experiments)) {
    return {
      skin: 'neutralStandard',
      type: 'solid',
      size: 'tiny',
      uppercase: false,
    };
  }
  if (isBundleApp(appId, bundleApps)) {
    return {
      skin: 'success',
      type: 'solid',
      size: 'tiny',
      uppercase: false,
    };
  }
  if (!canUsePremiumApp(app, siteIsPremium)) {
    return {
      skin: 'neutralDanger',
      type: 'solid',
      size: 'tiny',
      uppercase: false,
    };
  }

  if (
    isPendingApp(app) ||
    isNewVersionAvailable(app as ManagedApp) ||
    isSetupIncomplete(app as ManagedApp)
  ) {
    return {
      skin: 'neutralLight',
      type: 'outlined',
      size: 'tiny',
      uppercase: false,
    };
  }

  if (isEmbeddedScriptApp(app)) {
    switch (appEmbeddedScriptAppsStatus) {
      case embeddedScriptAppStatus.ENABLED:
        return {
          skin: 'success',
          type: 'outlined',
          size: 'tiny',
          uppercase: false,
          prefixIcon: <ConfirmSmall />,
        };
      default:
        return {
          skin: 'neutralDanger',
          type: 'outlined',
          size: 'tiny',
          uppercase: false,
        };
    }
  }
};

const shouldShowBadge = (
  app: ManagedApp,
  appEmbeddedScriptAppsStatus: embeddedScriptAppStatus,
  siteIsPremium: boolean,
  experiments: InjectedExperimentsProps['experiments'],
  bundleApps,
) => {
  const appId = app?.appId;

  if (isHotelsAndNotPending(app, experiments)) {
    return true;
  }

  if (!canUsePremiumApp(app, siteIsPremium)) {
    return true;
  }
  if (isPendingApp(app) || isBundleApp(appId, bundleApps)) {
    return true;
  }
  if (isNewVersionAvailable(app)) {
    return true;
  }
  if (isSetupIncomplete(app as ManagedApp)) {
    return true;
  }
  if (isEmbeddedScriptApp(app)) {
    return (
      appEmbeddedScriptAppsStatus &&
      appEmbeddedScriptAppsStatus === embeddedScriptAppStatus.DISABLED
    );
  }
  return false;
};
const shouldShowHotelsTooltip = (
  app: ManagedApp,
  experiments: InjectedExperimentsProps['experiments'],
) => {
  return isHotelsAndNotPending(app, experiments);
};

const shouldShowTooltip = (
  app: ManagedApp,
  appEmbeddedScriptAppsStatus: embeddedScriptAppStatus,
  siteIsPremium: boolean,
  experiments: InjectedExperimentsProps['experiments'],
  bundleApps,
) => {
  const appId = app?.appId;
  if (!canUsePremiumApp(app, siteIsPremium)) {
    return true;
  }

  if (isPendingApp(app) || isBundleApp(appId, bundleApps)) {
    return true;
  }
  if (isNewVersionAvailable(app)) {
    return true;
  }
  if (isSetupIncomplete(app as ManagedApp)) {
    return true;
  }
  return false;
};

const getTooltipContent = (
  app: ManagedApp,
  appEmbeddedScriptAppsStatus: embeddedScriptAppStatus,
  t: any,
  siteIsPremium: boolean,
  experiments: InjectedExperimentsProps['experiments'],
  siteIsBusinessFirstSite: boolean,
  bundleApps,
) => {
  const appId = app?.appId;
  const appName = app?.appInfo?.name;
  if (
    isBundleAppAndNotInstalled(appId, bundleApps) &&
    isBundleAppFromVoucher(appId, bundleApps)
  ) {
    return t('manage-apps.status.badge.tooltip-bundle-voucher-not-installed', {
      appName,
    });
  }

  if (isBundleAppAndNotInstalled(appId, bundleApps)) {
    return t('manage-apps.status.badge.tooltip-bundle-not-installed', {
      appName,
    });
  }

  if (
    isBundleAppAndInstalled(appId, bundleApps) &&
    isBundleAppFromVoucher(appId, bundleApps)
  ) {
    return t('manage-apps.status.badge.tooltip-bundle-installed-voucher', {
      appName,
    });
  }

  if (isBundleAppAndInstalled(appId, bundleApps)) {
    return t('manage-apps.status.badge.tooltip-bundle-installed', {
      appName,
    });
  }

  if (!canUsePremiumApp(app, siteIsPremium)) {
    return t('manage-apps.status.badge.tooltip-premium');
  }

  if (isNewVersionAvailable(app)) {
    return t('manage-apps.status.badge.tooltip-update-version');
  }

  if (isPendingApp(app)) {
    if (siteIsBusinessFirstSite) {
      return t('manage-apps.status.badge.tooltip-site-pending');
    }
    return t('manage-apps.status.badge.tooltip-pending');
  }

  if (isSetupIncomplete(app)) {
    return t('manage-apps.status.badge.tooltip-complete-setup');
  }

  if (isEmbeddedScriptApp(app)) {
    return t('manage-apps.status.badge.tooltip-missing', {
      appName,
    });
  }
};

function AppBadgeBase({
  app,
  appEmbeddedScriptAppsStatus,
  siteIsPremium,
  siteIsBusinessFirstSite,
  bundleApps,
}: IAppBadgeProps) {
  const t = useTranslation();
  const { experiments } = useExperiments();

  return (
    <Badge
      {...getTheme(
        app,
        appEmbeddedScriptAppsStatus,
        siteIsPremium,
        experiments,
        bundleApps,
      )}
      dataHook="app-section-app-badge"
    >
      {getLabel(
        app,
        appEmbeddedScriptAppsStatus,
        t,
        siteIsPremium,
        experiments,
        siteIsBusinessFirstSite,
        bundleApps,
      )}
    </Badge>
  );
}

export function AppBadge({
  app,
  appEmbeddedScriptAppsStatus,
  siteIsPremium,
  siteIsBusinessFirstSite,
  bundleApps,
}: IAppBadgeProps) {
  const { experiments } = useExperiments();
  const t = useTranslation();
  const [show, setShow] = useState(
    shouldShowBadge(
      app,
      appEmbeddedScriptAppsStatus,
      siteIsPremium,
      experiments,
      bundleApps,
    ),
  );
  const updateTimer = useRef(null);
  const firstRender = useRef(true);

  function setUpdate() {
    setShow(!firstRender.current);
    updateTimer.current = setTimeout(() => {
      setShow(false);
      updateTimer.current = null;
    }, 2000);
  }

  React.useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (
      !updateTimer.current &&
      appEmbeddedScriptAppsStatus === embeddedScriptAppStatus.ENABLED
    ) {
      setUpdate();
    } else {
      setShow(true);
    }
  }, [appEmbeddedScriptAppsStatus]);

  React.useEffect(() => {
    return () => {
      if (updateTimer.current) {
        clearTimeout(updateTimer.current);
      }
    };
  }, []);

  return show ? (
    shouldShowHotelsTooltip(app, experiments) ? (
      <div onClick={(e) => e.stopPropagation()}>
        <FloatingHelper
          appearance="light"
          placement="top"
          appendTo="scrollParent"
          initiallyOpened={false}
          target={
            <AppBadgeBase
              app={app}
              appEmbeddedScriptAppsStatus={appEmbeddedScriptAppsStatus}
              siteIsPremium={siteIsPremium}
              siteIsBusinessFirstSite={siteIsBusinessFirstSite}
              bundleApps={bundleApps}
            />
          }
          content={
            <FloatingHelper.Content
              title={t('manage-apps.status.badge.Hotels-title')}
              body={t('manage-apps.status.badge.Hotels-subtitle')}
              actionText={t('manage-apps.status.badge.Hotels-cta')}
              actionTheme="standard"
              onActionClick={() =>
                window.open(t('manage-apps.status.badge.Hotels-link'))
              }
            />
          }
        />
      </div>
    ) : shouldShowTooltip(
        app,
        appEmbeddedScriptAppsStatus,
        siteIsPremium,
        experiments,
        bundleApps,
      ) ? (
      <Tooltip
        appendTo="scrollParent"
        maxWidth={224}
        content={getTooltipContent(
          app,
          appEmbeddedScriptAppsStatus,
          t,
          siteIsPremium,
          experiments,
          siteIsBusinessFirstSite,
          bundleApps,
        )}
      >
        <AppBadgeBase
          app={app}
          appEmbeddedScriptAppsStatus={appEmbeddedScriptAppsStatus}
          siteIsPremium={siteIsPremium}
          siteIsBusinessFirstSite={siteIsBusinessFirstSite}
          bundleApps={bundleApps}
        />
      </Tooltip>
    ) : (
      <AppBadgeBase
        app={app}
        appEmbeddedScriptAppsStatus={appEmbeddedScriptAppsStatus}
        siteIsPremium={siteIsPremium}
        siteIsBusinessFirstSite={siteIsBusinessFirstSite}
        bundleApps={bundleApps}
      />
    )
  ) : (
    <div />
  );
}
